/**
 * F39 (Wave 2C) — admin placement detail + moderation page.
 *
 * Route: `/placement-approvals/:id`
 * Permission: the backend detail READ is gated on `placement_inventory:read`
 *             (rev-13 — support + finance can open the rows they can already
 *             list from /placements). The SPA route gate in App.tsx still
 *             declares `placement_approvals:review` (an embargoed follow-up
 *             relaxes it to `placement_inventory:read`). Approve/Reject actions
 *             are additionally gated client-side on
 *             `placement_approvals:approve`; the server re-enforces.
 *
 * Shows the placement's venue / type / scope / period / charge breakdown +
 * snapshot, the CREATIVE PREVIEW (banner image / sponsored headline + copy), an
 * AuditLogTimeline of the lifecycle, and the Approve / Reject CTAs when the
 * placement is `pending_approval`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  type AuditEvent,
  AuditLogTimeline,
  Badge,
  Banner,
  Button,
  Chip,
  CurrencyDisplay,
  EmptyState,
  PlacementStateBadge,
  Skeleton,
  Stat,
  StatusChip,
  useToast,
} from '@bravemove/ui-web';
import { adminPlacementsApi, type AdminPlacementDetailResponse } from '../../api/placements';
import { isPlacementBadgeState } from '../placements/placementBadge';
import { translateApiError } from '../../auth/errors';
import { useAuth } from '../../auth/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { RejectPlacementDialog } from '../../components/placements/RejectPlacementDialog';
import { CancelPlacementDialog } from '../../components/placements/CancelPlacementDialog';

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'Asia/Beirut',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function PlacementDetailPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const auth = useAuth();
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';

  const canApprove = auth.adminUser?.permissions?.includes('placement_approvals:approve') ?? false;

  const [detail, setDetail] = useState<AdminPlacementDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await adminPlacementsApi.placementDetail(id));
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDetail();
  }, [fetchDetail]);

  const auditEvents = useMemo<readonly AuditEvent[]>(() => {
    if (!detail) return [];
    const venueActor = {
      name: detail.venueName ?? t('placement:admin.detail.actor_venue', { defaultValue: 'Venue' }),
    };
    const adminActor = (name: string | null) => ({
      name: name ?? t('placement:admin.detail.actor_admin', { defaultValue: 'Admin' }),
    });
    const events: AuditEvent[] = [
      {
        id: 'purchased',
        at: detail.createdAt,
        kind: 'create',
        summary: t('placement:admin.detail.event_purchased', {
          defaultValue: 'Placement purchased',
        }),
        actor: venueActor,
      },
    ];
    if (detail.approvedAt) {
      events.push({
        id: 'approved',
        at: detail.approvedAt,
        kind: 'permission',
        summary: t('placement:admin.detail.event_approved', { defaultValue: 'Approved' }),
        actor: adminActor(detail.approvedByAdminDisplayName),
      });
    }
    if (detail.status === 'rejected' && detail.rejectedReason) {
      events.push({
        id: 'rejected',
        at: detail.updatedAt,
        kind: 'delete',
        summary: t('placement:admin.detail.event_rejected', { defaultValue: 'Rejected' }),
        details: detail.rejectedReason,
        actor: adminActor(null),
      });
    }
    if (detail.status === 'cancelled' && detail.cancelledReason) {
      events.push({
        id: 'cancelled',
        at: detail.updatedAt,
        kind: 'delete',
        summary: t('placement:admin.detail.event_cancelled', { defaultValue: 'Cancelled' }),
        details: detail.cancelledReason,
        actor: adminActor(detail.cancelledByAdminDisplayName),
      });
    }
    return events;
  }, [detail, t]);

  const onApprove = async () => {
    setApproving(true);
    try {
      await adminPlacementsApi.approvePlacement(id);
      toast.push({
        tone: 'success',
        title: t('placement:admin.detail.toast_approved', { defaultValue: 'Placement approved.' }),
      });
      await fetchDetail();
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setApproving(false);
    }
  };

  const onReject = async (reason: string) => {
    await adminPlacementsApi.rejectPlacement(id, { reason });
    toast.push({
      tone: 'success',
      title: t('placement:admin.detail.toast_rejected', { defaultValue: 'Placement rejected.' }),
    });
    await fetchDetail();
  };

  const onCancel = async (reason: string, grantCreditMinor?: number) => {
    await adminPlacementsApi.cancelPlacement(id, {
      reason,
      ...(grantCreditMinor !== undefined ? { grantCreditMinor } : {}),
    });
    toast.push({
      tone: 'success',
      title: t('placement:admin.detail.toast_cancelled', { defaultValue: 'Placement cancelled.' }),
    });
    await fetchDetail();
  };

  const isPending = detail?.status === 'pending_approval';
  const isCancellable = detail
    ? detail.status === 'scheduled' ||
      detail.status === 'active' ||
      detail.status === 'pending_approval'
    : false;
  const venueLabel = detail?.venueName ?? detail?.venueId ?? id;

  return (
    <div className="space-y-6" data-testid="placement-detail-page">
      <PageHeader
        title={venueLabel}
        eyebrow={
          detail
            ? t(`placement:type.${detail.placementType}`, { defaultValue: detail.placementType })
            : t('placement:admin.detail.eyebrow', { defaultValue: 'Placement' })
        }
        actions={
          detail && canApprove ? (
            <>
              {isPending ? (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setRejectOpen(true)}
                    data-testid="placement-detail-reject-cta"
                  >
                    {t('placement:admin.approvals.reject', { defaultValue: 'Reject' })}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    loading={approving}
                    onClick={() => void onApprove()}
                    data-testid="placement-detail-approve-cta"
                  >
                    {t('placement:admin.approvals.approve', { defaultValue: 'Approve' })}
                  </Button>
                </>
              ) : null}
              {!isPending && isCancellable ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCancelOpen(true)}
                  data-testid="placement-detail-cancel-cta"
                >
                  {t('placement:admin.monitor.override_cancel', {
                    defaultValue: 'Cancel placement',
                  })}
                </Button>
              ) : null}
            </>
          ) : detail && !canApprove ? (
            <Chip data-testid="placement-detail-readonly">
              {t('placement:admin.detail.readonly', { defaultValue: 'Read-only' })}
            </Chip>
          ) : null
        }
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="placement-detail-loading">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : !detail ? (
        <EmptyState
          title={t('placement:admin.detail.not_found', { defaultValue: 'Placement not found.' })}
        />
      ) : (
        <>
          {/* Summary */}
          <section
            className="grid grid-cols-2 gap-3 md:grid-cols-4"
            data-testid="placement-detail-summary"
          >
            <Stat
              label={t('placement:admin.detail.status', { defaultValue: 'Status' })}
              value={
                isPlacementBadgeState(detail.status) ? (
                  <PlacementStateBadge
                    size="sm"
                    state={detail.status}
                    label={t(`placement:status.${detail.status}`, { defaultValue: detail.status })}
                  />
                ) : (
                  <StatusChip
                    size="sm"
                    status="rejected"
                    label={t(`placement:status.${detail.status}`, { defaultValue: detail.status })}
                  />
                )
              }
            />
            <Stat
              label={t('placement:admin.detail.period', { defaultValue: 'Period' })}
              value={`${formatDate(detail.periodStart, locale)} · ${detail.weeks}w`}
            />
            <Stat
              label={t('placement:admin.detail.tier', { defaultValue: 'Tier at purchase' })}
              value={detail.tierCodeAtPurchase}
            />
            <Stat
              label={t('placement:admin.detail.net_charge', { defaultValue: 'Net charge' })}
              value={
                <CurrencyDisplay
                  amount={detail.netChargeMinor}
                  currency={detail.priceCurrency}
                  locale={locale}
                />
              }
            />
          </section>

          {/* Charge breakdown */}
          <section className="space-y-2" data-testid="placement-detail-charge">
            <h2 className="text-[14px] font-semibold text-ink">
              {t('placement:calculator.title', { defaultValue: 'Price breakdown' })}
            </h2>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-[12.5px] md:max-w-md">
              <dt className="text-ink-2">
                {t('placement:calculator.gross', { defaultValue: 'Subtotal' })}
              </dt>
              <dd className="text-end text-ink">
                <CurrencyDisplay
                  amount={detail.grossMinor}
                  currency={detail.priceCurrency}
                  locale={locale}
                />
              </dd>
              <dt className="text-ink-2">
                {t('placement:calculator.discount', {
                  percent: detail.discountPercent,
                  defaultValue: 'Tier discount ({percent}%)',
                })}
              </dt>
              <dd className="text-end text-ink">{detail.discountPercent}%</dd>
              <dt className="text-ink-2">
                {t('placement:calculator.credit_applied', { defaultValue: 'Credit applied' })}
              </dt>
              <dd className="text-end text-ink">
                <CurrencyDisplay
                  amount={detail.creditAppliedMinor}
                  currency={detail.priceCurrency}
                  locale={locale}
                />
              </dd>
              <dt className="font-semibold text-ink">
                {t('placement:calculator.net_charge', { defaultValue: 'You pay (off-platform)' })}
              </dt>
              <dd className="text-end font-semibold text-ink">
                <CurrencyDisplay
                  amount={detail.netChargeMinor}
                  currency={detail.priceCurrency}
                  locale={locale}
                />
              </dd>
            </dl>
          </section>

          {/* Creative preview */}
          <section className="space-y-2" data-testid="placement-detail-creative">
            <h2 className="text-[14px] font-semibold text-ink">
              {t('placement:admin.approvals.creative_preview', {
                defaultValue: 'Creative preview',
              })}
            </h2>
            {detail.creativeImageUrl ? (
              <img
                src={detail.creativeImageUrl}
                alt={detail.creativeHeadline ?? venueLabel}
                className="max-h-64 w-full max-w-xl rounded-lg border border-hairline object-cover"
                data-testid="placement-detail-creative-image"
              />
            ) : null}
            {detail.creativeHeadline ? (
              <p
                className="text-[14px] font-semibold text-ink"
                data-testid="placement-detail-creative-headline"
              >
                {detail.creativeHeadline}
              </p>
            ) : null}
            {detail.creativeCopy ? (
              <p className="text-[13px] text-ink-2" data-testid="placement-detail-creative-copy">
                {detail.creativeCopy}
              </p>
            ) : null}
            {!detail.creativeImageUrl && !detail.creativeHeadline && !detail.creativeCopy ? (
              <Badge tone="neutral" size="sm" data-testid="placement-detail-no-creative">
                {t('placement:admin.detail.no_creative', {
                  defaultValue: 'No creative for this type.',
                })}
              </Badge>
            ) : null}
          </section>

          {/* Lifecycle timeline */}
          <section className="space-y-3" data-testid="placement-detail-timeline">
            <h2 className="text-[14px] font-semibold text-ink">
              {t('placement:admin.detail.timeline_heading', { defaultValue: 'Lifecycle' })}
            </h2>
            <AuditLogTimeline events={auditEvents} />
          </section>
        </>
      )}

      {detail && rejectOpen ? (
        <RejectPlacementDialog
          open={rejectOpen}
          onConfirm={onReject}
          onClose={() => setRejectOpen(false)}
        />
      ) : null}

      {detail && cancelOpen ? (
        <CancelPlacementDialog
          open={cancelOpen}
          currency={detail.priceCurrency}
          onConfirm={onCancel}
          onClose={() => setCancelOpen(false)}
        />
      ) : null}
    </div>
  );
}

export default PlacementDetailPage;
